import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { CvService } from '../cv.service';
import { AuthService } from '../../shared/auth.service';
import { ClientCvTheme, CvCurationPayload, CvTemplate } from '../../interfaces/cv';
import { ClientCvTemplateService } from '../templates/client-cv-template.service';
import { ClientCvTemplate } from '../../interfaces/client-cv-template.interface';

/**
 * Admin card for configuring a client's CV theme: colors, font, logo
 * reference and sidebar side. Mounted on the client detail page alongside
 * the CV list.
 *
 * Retention is intentionally NOT exposed here: the underlying
 * default_retention_days column on ClientCvTheme is still honoured by
 * CvPersister, but the UI defers to the global config('cv.retention')
 * default to keep this card focused on look-and-feel.
 *
 * Phase 1 simplification: logo upload is exposed as a plain numeric
 * FileEntry id input. Wiring it to a proper uploader (bst-photo-uploader or
 * similar) is a follow-up once the FileEntry upload endpoint for theme
 * logos is defined.
 */
@Component({
  selector: 'bst-cv-theme-admin',
  templateUrl: './cv-theme-admin.component.html',
  styleUrls: ['./cv-theme-admin.component.scss'],
  standalone: false
})
export class CvThemeAdminComponent implements OnInit, OnDestroy {

  @Input() public clientId!: number;

  /** Allowlist of fonts kept in sync with backend config('cv.fonts'). */
  public readonly fonts: string[] = ['Inter', 'IBM Plex Sans', 'Source Serif 4'];

  /** Supported sidebar positions. */
  public readonly sidebarPositions: Array<'left' | 'right'> = ['left', 'right'];

  /**
   * Template gallery, kept in sync with backend config('cv.templates').
   * Each entry's `honours` controls which layout toggles are enabled in
   * the UI when that template is selected (see isToggleHonoured).
   */
  public readonly templates: CvTemplate[] = [
    {
      id: 'classic',
      name: 'Classic',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['header_photo', 'skills_format', 'experience_layout', 'references_line'],
    },
    {
      id: 'editorial',
      name: 'Editorial',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['sidebar_position', 'header_photo', 'skills_format', 'experience_layout', 'references_line'],
    },
    {
      id: 'minimal',
      name: 'Minimal',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['references_line'],
    },
    {
      id: 'editorial-plus',
      name: 'Editorial Plus',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['skills_format', 'experience_layout', 'references_line'],
    },
    {
      id: 'spotlight',
      name: 'Spotlight',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['sidebar_position', 'header_photo', 'skills_format', 'experience_layout', 'references_line'],
    },
  ];

  /**
   * Tenant-restricted templates (config client_domains). Listed only for a
   * client whose saved theme already uses one; the server enforces the rest.
   */
  private readonly restrictedTemplates: CvTemplate[] = [
    {
      id: 'verholt',
      name: 'Verholt Groep',
      supported_locales: ['nl', 'en'],
      sections: [],
      honours: ['sidebar_position', 'header_photo', 'skills_format', 'experience_layout', 'references_line'],
    },
  ];

  public form: FormGroup;
  public loading = false;
  public saving = false;
  public loaded = false;

  /** This client's ready+active uploaded templates, for the default-template picker. */
  public clientTemplates: ClientCvTemplate[] = [];

  // Live preview state. previewHtml is rendered into an iframe via srcdoc
  // whenever the form or template_id changes (debounced). previewLoading
  // covers the in-flight request window so the UI can indicate activity
  // without flickering the iframe off.
  public previewHtml: SafeHtml | null = null;
  public previewLoading = false;
  public previewError: string | null = null;
  private previewSub: Subscription | null = null;

  private subs: Subscription[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly cvService: CvService,
    private readonly clientCvTemplateService: ClientCvTemplateService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.form = this.fb.group({
      template_id: ['classic'],
      primary_color: ['#1863DC'],
      accent_color: ['#edf1f7'],
      text_color: ['#222b45'],
      font_family: ['Inter'],
      sidebar_position: ['left' as 'left' | 'right'],
      header_style: ['default'],
      header_photo: [false],
      skills_format: ['bullets' as 'inline' | 'bullets' | 'bullets_with_tools'],
      experience_layout: ['date_first' as 'role_first' | 'date_first'],
      references_line: ['on' as 'on' | 'off' | 'custom'],
      references_custom_text: [null as string | null],
      // Default template used for NEW CVs when nothing else is picked (see
      // App\Cv\CvTemplateSelection::resolveForClient()). 'client' requires
      // client_cv_template_id to point at one of this client's own ready+
      // active uploaded templates (enforced server-side too).
      template_type: ['built_in' as 'built_in' | 'client'],
      client_cv_template_id: [null as number | null],
    });
  }

  ngOnInit(): void {
    if (!this.clientId) {
      return;
    }
    this.load();

    // Debounced live-preview refresh on any form change. The preview HTML
    // payload is ~340KB (inlined fonts + page backgrounds), so re-renders
    // feel laggy at 300ms when the user drags the colour picker. 800ms
    // lets a typical pick or dropdown change settle before refiring, while
    // still feeling live.
    this.subs.push(this.form.valueChanges.pipe(
      debounceTime(800),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ).subscribe(() => this.refreshPreview()));

    // Re-evaluate the gated controls' enabled state whenever the active
    // template changes. Initial sync is done from load() once the form
    // has been seeded with the persisted template_id.
    this.subs.push(
      this.form.get('template_id')!.valueChanges.subscribe(
        (id: string) => this.syncDisabledControls(id),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    if (this.previewSub) { this.previewSub.unsubscribe(); }
  }

  /**
   * Fetch a fresh preview render using the current (possibly unsaved) form
   * values. Uses the logged-in user as the subject so the sample always
   * has rich content to render. Cancels any in-flight preview before
   * starting the next one so a fast-typing user doesn't accumulate stale
   * renders.
   */
  private refreshPreview(): void {
    const me = this.auth.user.getValue();
    if (!me?.id || !this.clientId || !this.loaded) {
      return;
    }

    if (this.previewSub) {
      this.previewSub.unsubscribe();
      this.previewSub = null;
    }

    const raw = this.form.value;
    const effective = this.effectiveTemplateSelection(raw);
    const payload: CvCurationPayload = {
      client_id: this.clientId,
      template_id: raw.template_id || 'classic',
      locale: 'en',
      // Top-level (not under theme): resolveSelection() reads these to
      // composite the uploaded template's background into the preview, same
      // fields the real create/redraft endpoints use.
      template_type: effective.template_type,
      client_cv_template_id: effective.client_cv_template_id,
      theme: {
        template_id: raw.template_id || undefined,
        primary_color: raw.primary_color || undefined,
        accent_color: raw.accent_color || undefined,
        text_color: raw.text_color || undefined,
        font_family: raw.font_family || undefined,
        sidebar_position: raw.sidebar_position || undefined,
        header_style: raw.header_style || undefined,
        header_photo: raw.header_photo ?? undefined,
        skills_format: raw.skills_format || undefined,
        experience_layout: raw.experience_layout || undefined,
        references_line: raw.references_line || undefined,
        references_custom_text: raw.references_line === 'custom'
          ? (raw.references_custom_text || null)
          : null,
      },
    };

    this.previewLoading = true;
    this.previewError = null;
    this.previewSub = this.cvService.preview(me.id, payload).subscribe({
      next: (html: string) => {
        this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
        this.previewLoading = false;
      },
      error: err => {
        console.error('cv-theme-admin: preview refresh failed', err);
        this.previewLoading = false;
        this.previewError = this.translate.instant('cv.theme.previewFailed')
          || 'Preview could not be rendered.';
      },
    });
  }

  /**
   * Fetch the existing theme (may be null) and seed the form. Missing
   * fields fall back to the sensible defaults already set in the form
   * constructor.
   */
  private load(): void {
    this.loading = true;
    this.subs.push(this.clientCvTemplateService.list(this.clientId).subscribe({
      next: page => {
        this.clientTemplates = (page.data || []).filter(t => t.status === 'ready' && t.is_active);
      },
      error: (err: unknown) => console.error('Failed to load client CV templates', err),
    }));
    const sub = this.cvService.getTheme(this.clientId).subscribe({
      next: (theme: ClientCvTheme | null) => {
        if (theme) {
          const restricted = this.restrictedTemplates.find(t => t.id === theme.template_id);
          if (restricted && !this.templates.includes(restricted)) {
            this.templates.push(restricted);
          }
          this.form.patchValue({
            template_id: theme.template_id ?? this.form.get('template_id')!.value,
            primary_color: theme.primary_color ?? this.form.get('primary_color')!.value,
            accent_color: theme.accent_color ?? this.form.get('accent_color')!.value,
            text_color: theme.text_color ?? this.form.get('text_color')!.value,
            font_family: this.fonts.includes(theme.font_family || '')
              ? theme.font_family
              : this.form.get('font_family')!.value,
            sidebar_position: theme.sidebar_position ?? this.form.get('sidebar_position')!.value,
            header_style: theme.header_style ?? this.form.get('header_style')!.value,
            header_photo: theme.header_photo ?? this.form.get('header_photo')!.value,
            skills_format: theme.skills_format ?? this.form.get('skills_format')!.value,
            experience_layout: theme.experience_layout ?? this.form.get('experience_layout')!.value,
            references_line: theme.references_line ?? this.form.get('references_line')!.value,
            references_custom_text: theme.references_custom_text ?? null,
            template_type: theme.template_type ?? 'built_in',
            client_cv_template_id: theme.client_cv_template_id ?? null,
          }, { emitEvent: false });
        }
        this.loaded = true;
        this.loading = false;
        // Apply the initial gated-control enable/disable state from the
        // seeded template_id. patchValue above used emitEvent: false, so
        // the template_id valueChanges subscription in ngOnInit didn't
        // fire — we have to sync explicitly here.
        this.syncDisabledControls(this.form.get('template_id')!.value);
        // Kick off the first preview render now that we have the seeded form.
        this.refreshPreview();
      },
      error: (err: unknown) => {
        console.error('Failed to load CV theme', err);
        this.loading = false;
        this.loaded = true;
        this.toastr.danger(
          this.translate.instant('cv.theme.errors.loadFailed')
          || 'Could not load the CV theme.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  /**
   * Persist the form values through CvService.updateTheme. Numeric and
   * nullable fields are normalised before sending so the backend gets
   * clean values.
   */
  public onSave(): void {
    if (this.saving || !this.clientId) {
      return;
    }

    const raw = this.form.value;
    const effective = this.effectiveTemplateSelection(raw);
    const body: Partial<ClientCvTheme> = {
      template_id: raw.template_id || undefined,
      primary_color: raw.primary_color || undefined,
      accent_color: raw.accent_color || undefined,
      text_color: raw.text_color || undefined,
      font_family: raw.font_family || undefined,
      sidebar_position: raw.sidebar_position || undefined,
      header_style: raw.header_style || undefined,
      header_photo: raw.header_photo ?? undefined,
      skills_format: raw.skills_format || undefined,
      experience_layout: raw.experience_layout || undefined,
      references_line: raw.references_line || undefined,
      references_custom_text: raw.references_line === 'custom'
        ? (raw.references_custom_text || null)
        : null,
      template_type: effective.template_type,
      client_cv_template_id: effective.client_cv_template_id ?? null,
    };

    this.saving = true;
    const sub = this.cvService.updateTheme(this.clientId, body).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(
          this.translate.instant('cv.theme.saved') || 'Theme saved.',
          this.translate.instant('cv.theme.title') || 'CV theme'
        );
      },
      error: (err: unknown) => {
        console.error('Failed to save CV theme', err);
        this.saving = false;
        this.toastr.danger(
          this.translate.instant('cv.theme.errors.saveFailed')
          || 'Could not save the CV theme.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  /**
   * template_type: 'client' with no client_cv_template_id fails both the
   * preview request's and the save request's server-side validator (a
   * 422/danger toast either way). Guards every payload built from the raw
   * form value against that pair ever going out desynced, regardless of how
   * it got into that state (a race, a direct patchValue, future FE bug).
   */
  private effectiveTemplateSelection(raw: { template_type?: string; client_cv_template_id?: number | null }):
    { template_type: 'built_in' | 'client'; client_cv_template_id?: number } {
    if (raw.template_type === 'client' && raw.client_cv_template_id) {
      return { template_type: 'client', client_cv_template_id: raw.client_cv_template_id };
    }
    return { template_type: 'built_in', client_cv_template_id: undefined };
  }

  /**
   * Flips template_type between 'built_in' and 'client'. Turning it on
   * pre-selects a template whenever at least one is ready (the picker's
   * [formControlName]="client_cv_template_id" select then lets the user
   * change it) -- template_type:'client' with no id fails both the preview
   * request's and the save request's server-side validator, so this must
   * never leave the pair desynced. Turning it off clears
   * client_cv_template_id so a stale id can't linger and get saved once the
   * toggle is off again. The template disables the toggle entirely when
   * clientTemplates is empty, but checked is still guarded here in case a
   * toggle event lands before that list finishes loading.
   */
  public onDefaultTemplateToggle(checked: boolean): void {
    if (checked && this.clientTemplates.length) {
      const preselect = this.form.value.client_cv_template_id ?? this.clientTemplates[0].id;
      this.form.patchValue({ template_type: 'client', client_cv_template_id: preselect });
    } else {
      this.form.patchValue({ template_type: 'built_in', client_cv_template_id: null });
    }
  }

  /**
   * True when the currently selected template declares it honours the
   * given toggle. Templates that don't react to a toggle (e.g. Minimal
   * ignores photo/skills/experience) render their control disabled.
   */
  public isToggleHonoured(toggle: string, templateId?: string): boolean {
    const id = templateId ?? this.form.get('template_id')?.value;
    const template = this.templates.find(t => t.id === id);
    if (!template?.honours) {
      return true;
    }
    return template.honours.includes(toggle);
  }

  /**
   * Apply per-template enable/disable to gated form controls so the UI
   * reflects which toggles the active template actually responds to.
   * Reactive forms ignore the `[disabled]` template attribute, so we
   * mutate the FormControls directly. emitEvent: false avoids a
   * valueChanges cascade with the live-preview debouncer.
   *
   * Note: disabled controls are excluded from form.value (used by
   * onSave), so unhonoured toggles are omitted from the save payload.
   * That matches backend behaviour — the server already ignores fields
   * the active template doesn't honour.
   */
  private syncDisabledControls(templateId: string): void {
    const gated: ReadonlyArray<string> = [
      'sidebar_position',
      'header_photo',
      'skills_format',
      'experience_layout',
    ];
    for (const name of gated) {
      const ctrl = this.form.get(name);
      if (!ctrl) { continue; }
      if (this.isToggleHonoured(name, templateId)) {
        ctrl.enable({ emitEvent: false });
      } else {
        ctrl.disable({ emitEvent: false });
      }
    }
  }

  // ------------------------------------------------------------------
  // WCAG AA contrast helpers
  //
  // We flag any foreground/background pair whose contrast ratio is below
  // the WCAG 2.1 AA threshold for normal body text (4.5:1). The icons in
  // the template only render when isLowContrast() returns true, so
  // everything here is pure and side-effect free. Invalid hex input (non
  // 6-digit form, missing #) defers to contrastRatio() returning 1, which
  // will trip the warning; that is intentional because an unparseable
  // color is itself a problem worth surfacing.
  // ------------------------------------------------------------------

  private hexToRgb(hex: string): [number, number, number] | null {
    if (!hex) return null;
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return null;
    const v = parseInt(m[1], 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }

  private luminance(hex: string): number {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return 0;
    const [r, g, b] = rgb.map(v => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  /** WCAG contrast ratio between two hex colors. Returns 1 on invalid input. */
  public contrastRatio(a: string, b: string): number {
    if (!a || !b) return 1;
    if (!this.hexToRgb(a) || !this.hexToRgb(b)) return 1;
    const la = this.luminance(a);
    const lb = this.luminance(b);
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);
    return (lighter + 0.05) / (darker + 0.05);
  }

  /** True when the two colors fall below the WCAG AA body-text ratio (4.5:1). */
  public isLowContrast(a: string, b: string): boolean {
    return this.contrastRatio(a, b) < 4.5;
  }
}
